/**
 * Web ProjectDetails Page
 * 
 * Clean composition layer using the Facade pattern.
 * 
 * Architecture:
 * - Uses shared useProjectDetailsPageFacade from @ideoz/core (zero duplication!)
 * - Injects web-specific implementations (navigation, auth, params)
 * - Passes facade directly to ProjectDetailsTemplate
 * 
 * Benefits:
 * - 90% less code (150+ lines → ~60 lines)
 * - Single source of truth for logic
 * - Easy to test (mock one facade)
 * - Platform-agnostic template
 */

import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ProjectDetailsTemplate, 
  showToast,
} from '@ideoz/ui';
import { useProjectDetailsPageFacade } from '@ideoz/core';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';
import { GOOGLE_AUTH_CONFIG } from '../config/googleAuth';

export default function ProjectDetails(): JSX.Element {
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  // Get mode from URL parameters (for plugin redirects)
  const urlMode = searchParams.get('mode') as 'prototyper' | 'uncover' | null;

  // ========== Platform-Specific Implementations ==========
  const navigation = useWebNavigation();
  const authDialog = useAuthDialog();

  // Adapt authDialog to AuthFacade interface
  const auth = React.useMemo(
    () => ({
      isOpen: authDialog.isOpen,
      mode: authDialog.mode,
      isLoading: authDialog.isLoading,
      openLogin: authDialog.openLogin,
      openRegister: authDialog.openRegister,
      close: authDialog.closeDialog,
    }),
    [authDialog]
  );

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useProjectDetailsPageFacade({
    navigation,
    auth,
    projectId,
    showToast,
    initialMode: urlMode || undefined, // Pass URL mode to facade for initialization
  });

  // ========== Render ==========
  return (
    <ProjectDetailsTemplate
      facade={facade}
      isWeb={true}
      t={t}
      googleAuthConfig={GOOGLE_AUTH_CONFIG}
      authDialogState={authDialog}
    />
  );
}
